import { useCallback, useState } from 'react';
import { apiErrorMessage } from '../../utils/apiErrors';

/** How many saves a bulk change has in flight at once. */
const CONCURRENCY = 4;

/**
 * Saving one field from a list (#603): the pickers on a violation or
 * work-order row. Given several rows — the selected ones — it saves the
 * same change to each, a few at a time. Keyed `${id}:${field}` so only
 * those rows' chips show they are busy.
 *
 * With the sync engine's copy, `save` goes through the write queue
 * (`sync/writes.ts`): the row shows the change at once, the chip is busy
 * until the server accepts it, and a refused change is taken back off.
 * Without a copy it is a plain request, and `apply` shows the answer. A
 * bulk change that partly fails keeps what succeeded and says how many
 * did not.
 */
export function useRowUpdate<T extends { id: string }, P>(
  save: (id: string, patch: P) => Promise<T | undefined>,
  apply: (updated: T) => void
) {
  const [saving, setSaving] = useState<ReadonlySet<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const update = useCallback(
    async (items: T[], patch: P, field: string) => {
      if (items.length === 0) return;
      const keys = items.map((item) => `${item.id}:${field}`);
      setSaving((prev) => new Set([...Array.from(prev), ...keys]));
      setError(null);

      const failures: unknown[] = [];
      const queue = [...items];
      const worker = async () => {
        for (let item = queue.shift(); item; item = queue.shift()) {
          try {
            const saved = await save(item.id, patch);
            if (saved) apply(saved);
          } catch (err) {
            console.error(`Error updating ${field} of ${item.id}:`, err);
            failures.push(err);
          } finally {
            const key = `${item.id}:${field}`;
            setSaving((prev) => {
              const next = new Set(prev);
              next.delete(key);
              return next;
            });
          }
        }
      };
      await Promise.all(
        Array.from({ length: Math.min(CONCURRENCY, items.length) }, worker)
      );

      if (failures.length > 0) {
        const reason = apiErrorMessage(
          failures[0],
          'The change was not saved. Please try again.'
        );
        setError(
          items.length === 1
            ? reason
            : `${failures.length} of ${items.length} changes were not saved. ${reason}`
        );
      }
    },
    [save, apply]
  );

  return { saving, error, clearError: () => setError(null), update };
}
